import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Unhooked Co offer widget.
 *
 * Prices are never hardcoded — every figure is derived from the variant data
 * Liquid handed us, so admin price changes, sales and currency conversion all
 * flow through with no template edit.
 *
 * Adding to cart mirrors product-form.js: POST the multi-line payload to
 * Theme.routes.cart_add_url with the cart section ids, then dispatch the
 * platform CartLinesUpdateEvent. The theme's cart drawer listens for exactly
 * that event and auto-opens on `action: 'add'`, so we get the drawer, the
 * cart bubble and the drawer re-render without touching theme files.
 */
class UnhookOffer extends HTMLElement {
  #data = null;
  #abort = new AbortController();
  #timer = null;

  connectedCallback() {
    const raw = this.querySelector('[data-unhook-data]');
    if (!raw) return;

    try {
      this.#data = JSON.parse(raw.textContent);
    } catch (error) {
      console.warn('[unhook-offer] Could not parse widget data:', error);
      return;
    }

    const { signal } = this.#abort;

    this.querySelectorAll('[data-bundle-input]').forEach((input) =>
      input.addEventListener('change', () => this.render(), { signal })
    );
    this.querySelector('[data-sub-input]')?.addEventListener('change', () => this.render(), { signal });
    this.querySelectorAll('select').forEach((select) =>
      select.addEventListener('change', (event) => this.#onVariantChange(event), { signal })
    );
    this.querySelector('[data-atc]')?.addEventListener('click', (event) => this.#add(event), { signal });

    /* a cart of nothing but freebies is not a cart */
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#tidyFreeLines, { signal });

    this.#startCountdown();
    this.render();
  }

  disconnectedCallback() {
    this.#abort.abort();
    if (this.#timer) clearInterval(this.#timer);
  }

  /* ---------------------------------------------------------------- money */

  get #format() {
    return this.dataset.moneyFormat || '{{amount}}';
  }

  get #currency() {
    return this.dataset.currency || 'GBP';
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.#format, this.#currency);
  }

  /**
   * Swap [savings] / [discount] placeholders for live figures.
   * Brackets rather than braces: Shopify validates a setting's default as a
   * dynamic source, and a brace placeholder is rejected at save time. The brace
   * form is still accepted here so older settings keep working.
   */
  #fill(template, { savings = 0, discount = 0 } = {}) {
    return String(template || '')
      .replace(/\[\s*savings\s*\]|\{\{\s*savings\s*\}\}/g, this.#money(savings))
      .replace(/\[\s*discount\s*\]|\{\{\s*discount\s*\}\}/g, `${discount}%`);
  }

  /* ------------------------------------------------------------- variants */

  #variant(group, id) {
    const list = this.#data?.[group]?.variants || [];
    return list.find((variant) => String(variant.id) === String(id)) || list[0] || null;
  }

  /** The selling plan the subscribe toggle should use, if any. */
  #plan(variant) {
    if (!this.#subscribed) return null;
    return variant?.sellingPlans?.[0] || null;
  }

  /** How many of option A's items the Shopify discount makes free. */
  get #freeItems() {
    return Number(this.dataset.freeItems || 0);
  }

  /** Whether an option is allowed to show a crossed-out compare-at price. */
  #showCompare(mode) {
    return this.dataset[mode === 'a' ? 'compareA' : 'compareB'] !== 'false';
  }

  /**
   * Mirror the Shopify discount on option B. Percentage first, then any fixed
   * amount, never below zero — the same order Shopify applies them in.
   * @param {number} cents
   */
  #discountB(cents) {
    const pct = Number(this.dataset.discountBPct || 0);
    const amt = Number(this.dataset.discountBAmt || 0);
    let out = cents;
    if (pct > 0) out -= Math.round((out * pct) / 100);
    if (amt > 0) out -= amt;
    return Math.max(0, out);
  }

  get #subscribed() {
    const input = this.querySelector('[data-sub-input]');
    return Boolean(input && input.checked && !input.disabled);
  }

  get #mode() {
    const checked = this.querySelector('[data-bundle-input]:checked');
    return checked ? checked.value : 'b';
  }


  /**
   * Apply a "N cheapest free" rule to a list of prices.
   * The widget only DISPLAYS this — the actual reduction comes from the
   * automatic discount configured in Shopify, which is why it zeroes the
   * cheapest lines, the same way Shopify's buy-X-get-Y does.
   * @param {number[]} prices
   * @param {number} free
   */
  #applyFree(prices, free) {
    if (!free) return prices.reduce((total, p) => total + p, 0);
    const sorted = [...prices].sort((a, b) => a - b);
    return sorted.slice(Math.min(free, sorted.length)).reduce((total, p) => total + p, 0);
  }

  /** Everything the current selection costs, and what it would have cost. */
  #quote() {
    const mode = this.#mode;
    const qty = this.#data.optionAQuantity || 1;

    if (mode === 'a') {
      const chosen = [];
      for (let i = 0; i < qty; i += 1) {
        const select = this.querySelector(`[data-a-select="${i}"]`);
        chosen.push(this.#variant('main', select?.value));
      }
      const price = this.#applyFree(chosen.map((v) => v?.price || 0), this.#freeItems);
      const compare = chosen.reduce((total, v) => total + (v?.compare || v?.price || 0), 0);
      return {
        mode,
        price,
        compare,
        subscribed: false,
        lines: chosen.filter(Boolean).map((v) => ({ id: v.id, quantity: 1 })),
      };
    }

    const mainVariant = this.#variant('main', this.querySelector('[data-b-main-select]')?.value);
    const compVariant = this.#variant('companion', this.querySelector('[data-b-comp-select]')?.value);
    const plan = this.#plan(compVariant);

    const compPrice = plan ? plan.price : compVariant?.price || 0;
    const compCompare = plan ? plan.compare || compVariant?.compare : compVariant?.compare;

    const lines = [];
    if (mainVariant) lines.push({ id: mainVariant.id, quantity: 1 });
    if (compVariant) {
      const line = { id: compVariant.id, quantity: 1 };
      if (plan) line.selling_plan = plan.id;
      lines.push(line);
    }

    return {
      mode,
      price: this.#discountB((mainVariant?.price || 0) + compPrice),
      compare: (mainVariant?.compare || mainVariant?.price || 0) + (compCompare || compPrice),
      subscribed: Boolean(plan),
      mainVariant,
      compVariant,
      plan,
      lines,
    };
  }

  /* --------------------------------------------------------------- render */

  render() {
    if (!this.#data) return;

    const quote = this.#quote();
    const savings = Math.max(0, quote.compare - quote.price);
    const discount = quote.compare > 0 ? Math.round((savings / quote.compare) * 100) : 0;
    const suffix = quote.subscribed ? this.#data.labels.perMonth : '';

    /* subscribe toggle only applies to the bundle that contains the companion */
    const subWrap = this.querySelector('[data-sub-wrap]');
    const subInput = this.querySelector('[data-sub-input]');
    if (subWrap && subInput) {
      const applies = quote.mode === 'b';
      subWrap.classList.toggle('is-off', !applies);
      subInput.disabled = !applies;
    }

    /* option A */
    const aQuote = this.#quoteFor('a');
    this.#text('[data-a-price]', this.#money(aQuote.price));
    this.#text(
      '[data-a-was]',
      this.#showCompare('a') && aQuote.compare > aQuote.price ? this.#money(aQuote.compare) : ''
    );
    const aSub = this.querySelector('[data-a-sub]');
    if (aSub && aSub.dataset.template === undefined) aSub.dataset.template = aSub.textContent.trim();
    if (aSub) {
      aSub.textContent = this.#fill(aSub.dataset.template, {
        savings: Math.max(0, aQuote.compare - aQuote.price),
      });
    }

    /* option B */
    const bQuote = this.#quoteFor('b');
    this.#text('[data-b-price]', this.#money(bQuote.price));
    const showB = this.#showCompare('b');
    this.#text('[data-b-was]', showB && bQuote.compare > bQuote.price ? this.#money(bQuote.compare) : '');
    if (!showB) {
      this.#text('[data-b-main-was]', '');
      this.#text('[data-b-comp-was]', '');
    }
    this.#text('[data-b-main-price]', this.#money(bQuote.mainVariant?.price || 0));
    this.#text('[data-b-comp-price]', this.#money(bQuote.compPrice));

    const flag = this.querySelector('[data-b-flag]');
    if (flag) {
      if (flag.dataset.template === undefined) flag.dataset.template = flag.textContent.trim();
      flag.textContent = this.#fill(flag.dataset.template, {
        savings: Math.max(0, bQuote.compare - bQuote.price),
      });
    }

    /* offer headline can carry the live discount */
    const headline = this.querySelector('[data-offer-headline]');
    if (headline) {
      if (headline.dataset.template === undefined) headline.dataset.template = headline.textContent.trim();
      headline.textContent = this.#fill(headline.dataset.template, { savings, discount });
    }

    /* button */
    const soldOut = quote.lines.length === 0;
    const button = this.querySelector('[data-atc]');
    if (button) button.disabled = soldOut;
    this.#text(
      '[data-atc-label]',
      soldOut ? this.#data.labels.soldOut : `${this.#data.labels.addToCart} — ${this.#money(quote.price)}${suffix}`
    );
  }

  /** Price a specific option without changing the current selection. */
  #quoteFor(mode) {
    const qty = this.#data.optionAQuantity || 1;

    if (mode === 'a') {
      const prices = [];
      let compare = 0;
      for (let i = 0; i < qty; i += 1) {
        const variant = this.#variant('main', this.querySelector(`[data-a-select="${i}"]`)?.value);
        prices.push(variant?.price || 0);
        compare += variant?.compare || variant?.price || 0;
      }
      return { price: this.#applyFree(prices, this.#freeItems), compare };
    }

    const mainVariant = this.#variant('main', this.querySelector('[data-b-main-select]')?.value);
    const compVariant = this.#variant('companion', this.querySelector('[data-b-comp-select]')?.value);
    const plan = this.#plan(compVariant);
    const compPrice = plan ? plan.price : compVariant?.price || 0;
    const compCompare = plan ? plan.compare || compVariant?.compare : compVariant?.compare;

    return {
      price: this.#discountB((mainVariant?.price || 0) + compPrice),
      compare: (mainVariant?.compare || mainVariant?.price || 0) + (compCompare || compPrice),
      mainVariant,
      compVariant,
      compPrice,
    };
  }

  #text(selector, value) {
    const node = this.querySelector(selector);
    if (!node) return;
    if (value === '') {
      node.hidden = true;
      return;
    }
    node.hidden = false;
    node.textContent = value;
  }

  /** Keep each row's thumbnail in step with the variant picked on that row. */
  #onVariantChange(event) {
    const select = event.currentTarget;
    const group = select.hasAttribute('data-b-comp-select') ? 'companion' : 'main';
    const variant = this.#variant(group, select.value);

    let thumb = null;
    if (select.hasAttribute('data-a-select')) {
      thumb = this.querySelector(`[data-a-thumb="${select.getAttribute('data-a-select')}"]`);
    } else if (select.hasAttribute('data-b-main-select')) {
      thumb = this.querySelector('[data-b-main-thumb]');
    } else if (select.hasAttribute('data-b-comp-select')) {
      thumb = this.querySelector('[data-b-comp-thumb]');
    }
    if (thumb && variant?.image) thumb.src = variant.image;

    this.render();
  }

  /* ------------------------------------------------------------ countdown */

  #startCountdown() {
    const bar = this.querySelector('[data-offer-bar]');
    if (!bar) return;

    const until = new Date(bar.dataset.until).getTime();
    if (!Number.isFinite(until)) return;

    const endLabel = this.querySelector('[data-offer-end]');
    if (endLabel) {
      endLabel.textContent = new Date(until).toLocaleDateString(document.documentElement.lang || 'en-GB', {
        day: 'numeric',
        month: 'long',
      });
    }

    const pad = (n) => String(n).padStart(2, '0');
    const cell = (unit) => this.querySelector(`[data-cd="${unit}"]`);

    const tick = () => {
      const left = until - Date.now();
      /* the offer removes itself rather than resetting — the urgency stays real */
      if (left <= 0) {
        bar.remove();
        clearInterval(this.#timer);
        return;
      }
      const d = Math.floor(left / 864e5);
      const h = Math.floor(left / 36e5) % 24;
      const m = Math.floor(left / 6e4) % 60;
      const s = Math.floor(left / 1e3) % 60;
      if (cell('d')) cell('d').textContent = pad(d);
      if (cell('h')) cell('h').textContent = pad(h);
      if (cell('m')) cell('m').textContent = pad(m);
      if (cell('s')) cell('s').textContent = pad(s);
    };

    tick();
    if (bar.isConnected) {
      bar.hidden = false;
      this.#timer = setInterval(tick, 1000);
    }
  }

  /* ------------------------------------------------------- free-line tidy */

  #tidying = false;

  /**
   * When the last paying item leaves the cart, take the £0.00 gifts with it.
   *
   * Paid is judged on original_line_price, not final_line_price: a
   * buy-one-get-one line ends up at £0.00 after the discount but is still a
   * real purchase, and removing it would be wrong.
   */
  #tidyFreeLines = async () => {
    if (this.#tidying) return;

    let cart;
    try {
      cart = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' }).then((r) => r.json());
    } catch {
      return;
    }

    const items = cart?.items || [];
    const paid = items.filter((i) => i.original_line_price > 0);
    const free = items.filter((i) => i.original_line_price === 0);
    if (paid.length > 0 || free.length === 0) return;

    this.#tidying = true;
    const updates = {};
    free.forEach((i) => {
      updates[i.key] = 0;
    });

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) sectionIds.push(node.dataset.sectionId);
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: free.map((i) => ({ merchandiseId: String(i.variant_id), quantity: 0 })),
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: sectionIds.join(',') }),
      });
      const json = await res.json();
      const fresh = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fresh),
        detail: {
          items: fresh.items,
          source: 'unhook-offer',
          sourceId: this.id,
          sections: json.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.warn('[unhook-offer] could not tidy free lines:', error);
      deferred.reject(error);
    } finally {
      this.#tidying = false;
    }
  };

  /* ----------------------------------------------------------------- cart */

  /**
   * The zero-priced products that ship with every order. They are appended to
   * the cart payload, never to the price maths — a £0.00 line cannot change a
   * total, and treating them as merchandise would distort the savings figure.
   */
  get #giftLines() {
    if (!this.#data?.addGifts) return [];
    return (this.#data.gifts || [])
      .filter(Boolean)
      .map((g) => ({ id: g.id, quantity: 1 }));
  }

  async #add(event) {
    const button = this.querySelector('[data-atc]');
    const errorEl = this.querySelector('[data-atc-error]');
    const quote = this.#quote();

    if (!quote.lines.length || button?.disabled) return;

    const lines = [...quote.lines, ...this.#giftLines];

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (errorEl) errorEl.hidden = true;

    /* the drawer re-renders from these section ids, exactly as product-form does */
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) sectionIds.push(node.dataset.sectionId);
    });

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({
          merchandiseId: String(line.id),
          quantity: line.quantity,
        })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines.map((line) => {
            const item = { id: Number(line.id), quantity: line.quantity };
            if (line.selling_plan) item.selling_plan = Number(line.selling_plan);
            return item;
          }),
          sections: sectionIds.join(','),
        }),
      });

      const json = await response.json();

      if (json.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: json.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: json.description, errors: json.errors },
          })
        );
        if (errorEl) {
          errorEl.textContent = json.description || json.message || 'Something went wrong.';
          errorEl.hidden = false;
        }
        deferred.resolve({ detail: { didError: true } });
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'unhook-offer',
          sourceId: this.id,
          itemCount: lines.reduce((total, line) => total + line.quantity, 0),
          sections: json.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[unhook-offer]', error);
      deferred.reject(error);
      if (errorEl) {
        errorEl.textContent = 'Network error — please try again.';
        errorEl.hidden = false;
      }
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('unhook-offer')) {
  customElements.define('unhook-offer', UnhookOffer);
}